"use client";

import type { DivisionId } from "@/lib/studio/studio";

interface MetronomeDivisionGlyphProps {
  id: DivisionId;
  /** Stroke / fill color for the notation. */
  color?: string;
  /** Secondary color for the small italic "swing" label etc. */
  dim?: string;
  /** Pixel width, height scales proportionally. */
  width?: number;
}

/**
 * Notation glyph for a beat subdivision, drawn with primitive SVG shapes
 * rather than a music font, so it stays crisp at any pixel size and
 * inherits the current color naturally.
 */
export function MetronomeDivisionGlyph({
  id,
  color = "currentColor",
  dim = "rgba(255, 255, 255, 0.35)",
  width = 36,
}: MetronomeDivisionGlyphProps) {
  const w = width;
  const h = Math.round((22 / 36) * width);

  // Helpers, scoped here so the JSX below stays readable.
  const head = (cx: number, cy = 18) => (
    <ellipse
      cx={cx}
      cy={cy}
      rx={2.6}
      ry={1.8}
      fill={color}
      transform={`rotate(-20 ${cx} ${cy})`}
    />
  );

  const stem = (x: number, top = 4, bottom = 18) => (
    <line x1={x} y1={top} x2={x} y2={bottom} stroke={color} strokeWidth={1.2} />
  );

  const beam = (x1: number, x2: number, y: number, thick = 2) => (
    <rect x={x1} y={y} width={x2 - x1} height={thick} fill={color} />
  );

  switch (id) {
    case "quarter":
      return (
        <svg width={w} height={h} viewBox="0 0 36 22" aria-hidden>
          {head(18)}
          {stem(20.6, 6, 18)}
        </svg>
      );

    case "eighths":
      return (
        <svg width={w} height={h} viewBox="0 0 36 22" aria-hidden>
          {head(11)}
          {stem(13.6, 4, 18)}
          {head(25)}
          {stem(27.6, 4, 18)}
          {beam(13.6, 28.8, 4, 2)}
        </svg>
      );

    case "triplet":
      return (
        <svg width={w} height={h} viewBox="0 0 36 22" aria-hidden>
          {head(7)}
          {stem(9.6, 4, 18)}
          {head(17.5)}
          {stem(20.1, 4, 18)}
          {head(28)}
          {stem(30.6, 4, 18)}
          {beam(9.6, 31.8, 4, 1.6)}
          <text
            x={20}
            y={2.5}
            fontSize={6}
            fill={color}
            fontFamily="var(--font-mono), JetBrains Mono, monospace"
            textAnchor="middle"
            fontWeight={700}
          >
            3
          </text>
        </svg>
      );

    case "sixteenth":
      return (
        <svg width={w} height={h} viewBox="0 0 36 22" aria-hidden>
          {head(6)}
          {stem(8.6, 4, 18)}
          {head(14)}
          {stem(16.6, 4, 18)}
          {head(22)}
          {stem(24.6, 4, 18)}
          {head(30)}
          {stem(32.6, 4, 18)}
          {beam(8.6, 33.8, 4, 1.6)}
          {beam(8.6, 33.8, 7, 1.6)}
        </svg>
      );

    case "dotted":
      // Dotted 8th + 16th
      return (
        <svg width={w} height={h} viewBox="0 0 36 22" aria-hidden>
          {head(10)}
          {stem(12.6, 4, 18)}
          <circle cx={15.5} cy={17} r={0.9} fill={color} />
          {head(26)}
          {stem(28.6, 4, 18)}
          {beam(12.6, 29.8, 4, 1.8)}
          <line
            x1={26.6}
            y1={6}
            x2={29.4}
            y2={6}
            stroke={color}
            strokeWidth={1.5}
          />
        </svg>
      );

    case "swing":
      return (
        <svg width={w} height={h} viewBox="0 0 36 22" aria-hidden>
          {head(11)}
          {stem(13.6, 4, 18)}
          {head(25)}
          {stem(27.6, 4, 18)}
          {beam(13.6, 28.8, 4, 2)}
          <text
            x={18}
            y={2.5}
            fontSize={5.5}
            fill={dim}
            fontFamily="var(--font-sans), Inter, sans-serif"
            fontStyle="italic"
          >
            swing
          </text>
        </svg>
      );

    default:
      return null;
  }
}
